import { useEffect, useRef, useState } from 'react';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';
import ReactMarkdown from 'react-markdown';

// Render assistant/agent text (markdown) with compact styling that fits the chat bubble.
const mdComponents = {
  p: (p: any) => <p style={{ margin: '0 0 6px' }}>{p.children}</p>,
  ul: (p: any) => <ul style={{ margin: '0 0 6px', paddingLeft: 18 }}>{p.children}</ul>,
  ol: (p: any) => <ol style={{ margin: '0 0 6px', paddingLeft: 18 }}>{p.children}</ol>,
  li: (p: any) => <li style={{ marginBottom: 2 }}>{p.children}</li>,
  h1: (p: any) => <p style={{ margin: '0 0 6px', fontWeight: 600 }}>{p.children}</p>,
  h2: (p: any) => <p style={{ margin: '0 0 6px', fontWeight: 600 }}>{p.children}</p>,
  h3: (p: any) => <p style={{ margin: '0 0 6px', fontWeight: 600 }}>{p.children}</p>,
  a: (p: any) => <a href={p.href} target="_blank" rel="noreferrer" style={{ color: 'inherit', textDecoration: 'underline' }}>{p.children}</a>,
  code: (p: any) => <code style={{ background: 'rgba(0,0,0,0.06)', padding: '1px 4px', borderRadius: 4 }}>{p.children}</code>,
};

type Sender = 'user' | 'ai' | 'agent';

interface Msg {
  id: number;
  content: string;
  sender: Sender;
}

interface Cfg {
  enabled: boolean;
  ai: boolean;
  human: boolean;
}

// Chatwoot thread message shape (human replies).
interface ThreadMsg {
  id: number;
  content: string;
  created_at: number;
  agent: boolean;
}

/**
 * In-app support chat. The AI assistant answers first (RAG over the help center);
 * if it doesn't help, the merchant taps "Contact support" to hand off to a human
 * via Chatwoot, whose replies then poll into the same thread. Hidden unless the
 * backend reports at least one support mode is available.
 */
export function SupportChat() {
  const [cfg, setCfg] = useState<Cfg>({ enabled: false, ai: false, human: false });
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState<'ai' | 'human'>('ai');
  const listRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const seenAgent = useRef<Set<number>>(new Set());
  const nextId = useRef(1);

  const add = (content: string, sender: Sender) =>
    setMessages((m) => [...m, { id: nextId.current++, content, sender }]);

  useEffect(() => {
    client
      .get<Cfg>(Endpoints.support.config)
      .then((r) => setCfg(r.data))
      .catch(() => setCfg({ enabled: false, ai: false, human: false }));
  }, []);

  // Once escalated to a human, poll Chatwoot for agent replies and merge them in.
  const loadThread = async () => {
    try {
      const r = await client.get<{ messages: ThreadMsg[] }>(Endpoints.support.thread);
      for (const m of r.data.messages ?? []) {
        if (m.agent && !seenAgent.current.has(m.id)) {
          seenAgent.current.add(m.id);
          add(m.content, 'agent');
        }
      }
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (open && mode === 'human') {
      loadThread();
      pollRef.current = setInterval(loadThread, 5000);
    }
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [open, mode]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setBusy(true);
    add(text, 'user');
    setInput('');
    try {
      if (mode === 'human') {
        await client.post(Endpoints.support.message, { message: text });
      } else {
        const r = await client.post<{ answer: string }>(Endpoints.support.ask, { message: text });
        add(r.data.answer, 'ai');
      }
    } catch {
      add("Sorry, something went wrong. Please try again.", 'ai');
    } finally {
      setBusy(false);
    }
  };

  // Hand off to a human: forward the last question to Chatwoot and switch to live mode.
  const contactSupport = async () => {
    if (busy) return;
    setBusy(true);
    const lastQuestion = [...messages].reverse().find((m) => m.sender === 'user')?.content;
    try {
      const intro = lastQuestion
        ? `A merchant needs help. Their question: ${lastQuestion}`
        : 'A merchant would like to talk to the support team.';
      await client.post(Endpoints.support.message, { message: intro });
      setMode('human');
      add("You're now connected to our support team — they'll reply here shortly. 🙌", 'agent');
    } catch {
      add("Couldn't reach the support team just now. Please try again in a moment.", 'ai');
    } finally {
      setBusy(false);
    }
  };

  if (!cfg.enabled) return null;

  const showEscalate = cfg.human && mode === 'ai' && messages.some((m) => m.sender === 'ai');
  const headerLabel = mode === 'human' ? 'ProfitVault support' : 'ProfitVault assistant';
  const placeholder = mode === 'human' ? 'Message the support team…' : 'Ask about profit, costs, ad spend…';

  const bubbleColor = (s: Sender) =>
    s === 'user' ? '#008060' : s === 'agent' ? '#303030' : '#ffffff';
  const textColor = (s: Sender) => (s === 'user' || s === 'agent' ? '#fff' : '#202223');

  return (
    <div style={{ position: 'fixed', right: 20, bottom: 20, zIndex: 400 }}>
      {open && (
        <div
          style={{
            width: 340,
            height: 460,
            marginBottom: 12,
            background: '#fff',
            border: '1px solid #e1e3e5',
            borderRadius: 12,
            boxShadow: '0 8px 30px rgba(0,0,0,0.16)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '12px 16px', background: '#303030', color: '#fff', fontWeight: 600 }}>
            {headerLabel}
            {mode === 'ai' && cfg.ai && (
              <span style={{ fontWeight: 400, fontSize: 12, opacity: 0.7, marginLeft: 6 }}>· AI</span>
            )}
          </div>
          <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 12, background: '#f6f6f7' }}>
            {messages.length === 0 && (
              <p style={{ color: '#6d7175', fontSize: 13, textAlign: 'center', marginTop: 24 }}>
                {cfg.ai
                  ? 'Ask me anything about ProfitVault — costs, profit, ad spend, plans…'
                  : 'Ask us anything — we’ll reply here.'}
              </p>
            )}
            {messages.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: m.sender === 'user' ? 'flex-end' : 'flex-start',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    maxWidth: '80%',
                    padding: '8px 12px',
                    borderRadius: 12,
                    fontSize: 14,
                    background: bubbleColor(m.sender),
                    color: textColor(m.sender),
                    border: m.sender === 'ai' ? '1px solid #e1e3e5' : 'none',
                    whiteSpace: m.sender === 'user' ? 'pre-wrap' : 'normal',
                  }}
                >
                  {m.sender === 'user' ? m.content : <ReactMarkdown components={mdComponents}>{m.content}</ReactMarkdown>}
                </div>
              </div>
            ))}
            {busy && mode === 'ai' && (
              <div style={{ color: '#6d7175', fontSize: 12, fontStyle: 'italic', marginLeft: 4 }}>
                Assistant is typing…
              </div>
            )}
            {showEscalate && (
              <div style={{ textAlign: 'center', marginTop: 4 }}>
                <button
                  onClick={contactSupport}
                  disabled={busy}
                  style={{
                    background: 'none',
                    border: '1px solid #c9cccf',
                    borderRadius: 999,
                    padding: '6px 14px',
                    fontSize: 12,
                    color: '#303030',
                    cursor: 'pointer',
                  }}
                >
                  Didn’t help? Contact support
                </button>
              </div>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8, padding: 10, borderTop: '1px solid #e1e3e5' }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && send()}
              placeholder={placeholder}
              style={{ flex: 1, border: '1px solid #c9cccf', borderRadius: 8, padding: '8px 10px', fontSize: 14 }}
            />
            <button
              onClick={send}
              disabled={busy}
              style={{
                background: '#008060',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                padding: '8px 14px',
                fontSize: 14,
                cursor: 'pointer',
                opacity: busy ? 0.6 : 1,
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}

      <button
        onClick={() => setOpen((o) => !o)}
        aria-label="Support chat"
        style={{
          width: 56,
          height: 56,
          borderRadius: '50%',
          background: '#008060',
          color: '#fff',
          border: 'none',
          fontSize: 24,
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(0,0,0,0.25)',
          marginLeft: 'auto',
          display: 'block',
        }}
      >
        {open ? '×' : '💬'}
      </button>
    </div>
  );
}
